import {
	type ColorRamp,
	type ColorRampKind,
	getRampKeys,
	hexToRgb,
	isValidHex,
} from "@offstreetparking/domain/value/branding";
import {
	OFFSTREET_ACCENT_RAMP,
	OFFSTREET_PRIMARY_RAMP,
	type RampStop,
	generateColorVariablesFromRamp,
} from "./theme-color-helper";

// Source-of-truth hex for the canonical Offstreet brand surface (.700 stop).
// Consumed by branding forms and public-form fallbacks elsewhere in the app.
export const OFFSTREET_DEFAULT_PRIMARY_COLOR_HEX = "#06653C";
export const OFFSTREET_FORM_ACTION_COLOR = "#146EEB";
export const OFFSTREET_COMPANY_LOGO = "https://offstreet.s3.us-west-2.amazonaws.com/logos/Offstreet_Logo_FullColor.png";
export const OFFSTREET_FAVICON_LOGO =
	"https://offstreet.s3.us-west-2.amazonaws.com/email/offstreet_logo_favicon_light-32x32.png";

type ThemeProviderProps = {
	branding?: {
		id: number;
		primaryColorRamp: ColorRamp;
		accentColorRamp: ColorRamp;
	} | null;
};

// DS expects RGB triplets; ramps store hex. Iterates the canonical key list so a corrupt or
// missing stop is always backfilled from `defaults` (never undefined in the emitted CSS).
const hexRampToRgbRamp = (
	ramp: ColorRamp,
	defaults: Record<RampStop, string>,
	kind: ColorRampKind,
): Record<RampStop, string> => {
	const out = {} as Record<RampStop, string>;
	for (const key of getRampKeys(kind)) {
		const hex = ramp.stops[key];
		if (hex && isValidHex(hex)) {
			const { r, g, b } = hexToRgb(hex);
			out[key] = `${r}, ${g}, ${b}`;
		} else {
			out[key] = defaults[key];
		}
	}
	return out;
};

const primaryCssFor = (ramp: ColorRamp) =>
	generateColorVariablesFromRamp(hexRampToRgbRamp(ramp, OFFSTREET_PRIMARY_RAMP, "primary"), "primary");

const accentCssFor = (ramp: ColorRamp) =>
	generateColorVariablesFromRamp(hexRampToRgbRamp(ramp, OFFSTREET_ACCENT_RAMP, "accent"), "accent");

export const ThemeProvider = ({ branding }: ThemeProviderProps) => {
	if (!branding) {
		return (
			// @ts-ignore - `precedence` is valid in nextjs
			<style type="text/css" precedence="default" href="default-theme">
				{`
                :root {
                    ${generateColorVariablesFromRamp(OFFSTREET_PRIMARY_RAMP, "primary")}
                    ${generateColorVariablesFromRamp(OFFSTREET_ACCENT_RAMP, "accent")}
				}
            `}
			</style>
		);
	}

	return (
		// @ts-ignore - `precedence` is valid in nextjs
		<style type="text/css" precedence="default" href={`unique-${branding.id}`}>
			{`
                :root {
                    ${primaryCssFor(branding.primaryColorRamp)}
                    ${accentCssFor(branding.accentColorRamp)}
				}
            `}
		</style>
	);
};
